// One draw call per tile for every fragment that appears exactly once in
// the whole model.
//
// Batching (tileBatch.js) folds a tile's rare fragments into one
// BatchedMesh, but each item still costs a copy of its geometry and a
// slot in a multi-draw. The exporter can do better for the extreme case:
// a fragment used by a single element anywhere in the model has nothing
// to instance against, so it is pre-merged, per tile and per level of
// detail, into one mesh with its transform already baked in. On the live
// SpaceX export that is 58,215 elements across 1,039 tiles - 12.0M
// triangles at medium and coarse - and it replaces 58,215 pools with one
// mesh per tile.
//
// The merged mesh is a plain Mesh, so it has no per-item anything. What
// makes it usable anyway is the per-vertex element index the exporter
// writes (`_ELEMENTINDEX`, which GLTFLoader hands us lowercased) and the
// uid table the file carries in its own glTF scene extras. Together they
// give every vertex an identity, and a float texture indexed by it gives
// every element its own visibility and tint:
//
//   texel.a < 0.5  -> the vertex collapses to a point, so its triangle
//                     has no area and rasterises nothing
//   texel.rgb      -> multiplied into vColor, which is how selection,
//                     the ghosted bucket and the revision overlay read
//
// That is enough for the mesh to pass for a slot-backed mesh: the state
// applicator writes either a snapshot matrix or a zero-scale sentinel per
// item, and here a zero-scale write means "hide this element".
import {
  DataTexture, FloatType, Float32BufferAttribute, Group, Matrix4, Mesh, MeshBasicMaterial, RGBAFormat,
} from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { SLOT_GHOST_ALPHA, slotHalftoneFrag } from './ghostMaterial';
import { markSceneChanged } from './renderSignal';
import { GLSL_APPLY_STATE_COLOR } from './stateColor';
import { applySurfaceShading } from './surfaceShading';

// GLTFLoader lowercases attribute names it does not know, so the
// exporter's _ELEMENTINDEX arrives as _elementindex. Accept what the
// exporter wrote as well, in case that ever changes.
const ELEMENT_ATTRIBUTES = ['_elementindex', '_ELEMENTINDEX', 'elementindex'];

const DECL = `
uniform highp sampler2D mergeStateTexture;
attribute float _elementindex;
vec4 getMergeState( const in float i ) {
  int size = textureSize( mergeStateTexture, 0 ).x;
  int j = int( i );
  int x = j % size;
  int y = j / size;
  return texelFetch( mergeStateTexture, ivec2( x, y ), 0 );
}
varying vec4 vMergeState;
`;
const FRAG_DECL = 'varying vec4 vMergeState;\n';

// Placed after begin_vertex so `transformed` exists: collapsing it is
// what removes a hidden element, and it costs nothing for visible ones.
const HIDE = `
vMergeState = getMergeState( _elementindex );
if ( vMergeState.a < 0.5 ) {
  transformed = vec3( 0.0 );
}
`;

// A state colour WINS rather than tinting - see stateColor.js. Merged
// meshes carry the same rule as pools and batches so a selection looks the
// same whichever way an element happens to be drawn.
const TINT = `
vColor.rgb = blApplyStateColor( vColor.rgb, vMergeState.rgb );
`;

function makeStateTexture(count) {
  let size = Math.ceil(Math.sqrt(Math.max(1, count)));
  size = Math.max(4, size);
  const data = new Float32Array(size * size * 4);
  // White and visible, so an element nobody has touched renders as the
  // exporter meant it to.
  data.fill(1);
  const texture = new DataTexture(data, size, size, RGBAFormat, FloatType);
  texture.needsUpdate = true;
  return texture;
}

function patchMaterial(material, texture) {
  material.userData = material.userData || {};
  material.userData.__mergeState = texture;
  const previous = material.onBeforeCompile;
  material.onBeforeCompile = (shader, renderer) => {
    if (typeof previous === 'function') previous(shader, renderer);
    shader.uniforms.mergeStateTexture = { value: texture };
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\n' + GLSL_APPLY_STATE_COLOR + DECL)
      .replace('#include <begin_vertex>', '#include <begin_vertex>\n' + HIDE)
      .replace('#include <color_vertex>', '#include <color_vertex>\n' + TINT);
    // alpha 2 = ghosted: drawn as the halftone (ghostMaterial.slotHalftoneFrag).
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\n' + FRAG_DECL)
      .replace('#include <color_fragment>', '#include <color_fragment>\n' + slotHalftoneFrag('( vMergeState.a > 1.5 ? 1.0 : 0.0 )'));
  };
  const prevKey = typeof material.customProgramCacheKey === 'function'
    ? material.customProgramCacheKey.bind(material) : null;
  material.customProgramCacheKey = () => (prevKey ? prevKey() : '') + '|mergeState2';
  material.needsUpdate = true;
  return material;
}

function readTable(gltf) {
  const places = [
    gltf && gltf.scene && gltf.scene.userData,
    gltf && gltf.userData,
    gltf && gltf.parser && gltf.parser.json && gltf.parser.json.scenes
      && gltf.parser.json.scenes[0] && gltf.parser.json.scenes[0].extras,
  ];
  for (const place of places) {
    const table = place && place.bidlight;
    if (table && Array.isArray(table.uids)) return table;
  }
  return null;
}

// Where each element sits and how big it is, so the viewer can apply the
// same pixel floor to a merged element that it applies to a fragment.
//
// Without this a merged tile is all-or-nothing: claiming its coverage
// draws every element in it, including the small ones the fragment path
// was skipping at that distance. Measured at an aerial view of SpaceX,
// that tripled the triangles - 1.2M to 4.1M - because half the
// singletons are exactly what the pixel floor was skipping.
//
// The exporter can ship these in extras, and when it does they are used
// as given. Otherwise they are derived here: every vertex already
// carries its element index, so one pass over the positions gives each
// element its own box.
export function itemBounds(geometry, count, table) {
  if (table && table.centres && table.radii
      && table.centres.length >= count * 3 && table.radii.length >= count) {
    return { centres: Float32Array.from(table.centres), radii: Float32Array.from(table.radii) };
  }
  const position = geometry.getAttribute('position');
  const indices = geometry.getAttribute('_elementindex');
  if (!position || !indices) return null;

  const min = new Float32Array(count * 3).fill(Infinity);
  const max = new Float32Array(count * 3).fill(-Infinity);
  for (let v = 0; v < position.count; v++) {
    const item = indices.getX(v);
    if (!(item >= 0 && item < count)) continue;
    const o = item * 3;
    const x = position.getX(v);
    const y = position.getY(v);
    const z = position.getZ(v);
    if (x < min[o]) min[o] = x;
    if (y < min[o + 1]) min[o + 1] = y;
    if (z < min[o + 2]) min[o + 2] = z;
    if (x > max[o]) max[o] = x;
    if (y > max[o + 1]) max[o + 1] = y;
    if (z > max[o + 2]) max[o + 2] = z;
  }

  const centres = new Float32Array(count * 3);
  const radii = new Float32Array(count);
  for (let i = 0; i < count; i++) {
    const o = i * 3;
    if (!Number.isFinite(min[o])) continue;
    const dx = max[o] - min[o];
    const dy = max[o + 1] - min[o + 1];
    const dz = max[o + 2] - min[o + 2];
    centres[o] = min[o] + dx / 2;
    centres[o + 1] = min[o + 1] + dy / 2;
    centres[o + 2] = min[o + 2] + dz / 2;
    // Half the box diagonal, which is the same quantity the fragments
    // index reports per hash.
    radii[i] = Math.sqrt(dx * dx + dy * dy + dz * dz) / 2;
  }
  return { centres, radii };
}

function firstMesh(root) {
  let found = null;
  root.traverse((object) => { if (!found && object.isMesh) found = object; });
  return found;
}

const parser = new GLTFLoader();

// Parsed in a worker when one is available.
//
// On the main thread this was the whole cost of merging: a thousand
// merged files, each parsed where the frame is drawn, so the loader spent
// its budget on merges and ordinary tiles stopped becoming visible - 237
// of them frozen for ninety seconds while merged items climbed to 40,771.
// The worker pool that parses every other GLB does this one too now; it
// needed only the element-index attribute kept and the scene extras
// returned.
//
// The main-thread path stays as the fallback for a browser with no
// workers, which is the same fallback GLBFetcher has always had.
export async function parseMergeBuffer(buffer, workerPool = null) {
  if (workerPool && workerPool.available) {
    try {
      const parts = await workerPool.parseGLB(buffer);
      const part = Array.isArray(parts) ? parts.find((p) => p && p.geometry) : null;
      if (part && part.geometry) {
        // A REAL Mesh in a REAL Group.
        //
        // The first version of this handed back plain objects that merely
        // looked like a scene and a mesh, and everything downstream that
        // treats them as three objects broke on contact: Object3D.add
        // refused the mesh so it was never in the scene, and
        // updateMatrixWorld threw, which the loader caught and counted as
        // a merge that would not load. Duck typing a scene graph does not
        // survive the first method call.
        // A part that arrived without a material falls back to a plain
        // one - which has no normal, and used to be refused by the
        // shading patch. Both are patched now, so the fallback looks
        // like everything else rather than being the one silent gap.
        const mesh = new Mesh(part.geometry, applySurfaceShading(part.material || new MeshBasicMaterial()));
        const scene = new Group();
        scene.add(mesh);
        scene.userData = (parts && parts.extras) || {};
        return { scene };
      }
    } catch (e) {
      // Fall through to the main thread rather than losing the merge.
    }
  }
  return new Promise((resolve, reject) => {
    try {
      parser.parse(buffer, '', resolve, reject);
    } catch (e) {
      reject(e);
    }
  });
}

// rowsByUid: uid -> the tile's sidecar row, so a merged element carries
// the same record the pool path would have given it.
export function buildMergeMesh({ gltf, tileKey, lodName, lodIndex, rowsByUid, sceneRoot, viewer }) {
  const table = readTable(gltf);
  const mesh = gltf && gltf.scene ? firstMesh(gltf.scene) : null;
  if (!table || !mesh || !mesh.geometry) return null;

  const uids = table.uids;
  const geometry = mesh.geometry;

  let elementAttribute = null;
  for (const name of ELEMENT_ATTRIBUTES) {
    if (geometry.getAttribute(name)) { elementAttribute = name; break; }
  }
  if (!elementAttribute) return null;

  // The exporter writes the index as uint32. A float copy sidesteps every
  // question about how an integer attribute reaches a float in the
  // shader, and the values here are per-tile item indices, far inside
  // what a float holds exactly.
  const source = geometry.getAttribute(elementAttribute);
  if (elementAttribute !== '_elementindex' || !(source.array instanceof Float32Array)) {
    const floats = new Float32Array(source.count);
    for (let i = 0; i < source.count; i++) floats[i] = source.getX(i);
    geometry.setAttribute('_elementindex', new Float32BufferAttribute(floats, 1));
    if (elementAttribute !== '_elementindex') geometry.deleteAttribute(elementAttribute);
  }

  const texture = makeStateTexture(uids.length);
  const material = mesh.material;
  material.vertexColors = true;
  patchMaterial(material, texture);
  applySurfaceShading(material);

  mesh.name = `merge:${tileKey}:${lodName}`;
  mesh.frustumCulled = false;

  const snapshotMatrices = new Float32Array(uids.length * 16);
  const identity = new Matrix4();
  const baseColors = new Float32Array(uids.length * 3);
  baseColors.fill(1);
  const userData = {};
  userData.snapshotMatrices = snapshotMatrices;
  userData.centeredMatrices = snapshotMatrices;
  userData.baseColors = baseColors;
  userData.tileKey = tileKey;
  userData.isTileMerge = true;

  const records = new Map();
  const uidSet = new Set();
  const indexByUid = new Map();
  for (let i = 0; i < uids.length; i++) {
    const uid = uids[i];
    uidSet.add(uid);
    indexByUid.set(uid, i);
    // The transform is baked into the vertices, so the matrix an item
    // reports is the identity. It is here because the applicator reads
    // it back for a visible item.
    identity.toArray(snapshotMatrices, i * 16);
    const row = (rowsByUid && rowsByUid.get(uid)) || { uniqueId: uid };
    row.id = uid;
    row.index = i;
    row.tileKey = tileKey;
    userData[i] = row;
    records.set(uid, {
      mesh,
      merge: mesh,
      uniqueId: uid,
      id: uid,
      index: i,
      fragments: [{ mesh, index: i, merge: mesh }],
    });
  }
  mesh.userData = userData;

  const data = texture.image.data;

  // An element is drawn only when BOTH agree: the state store (hidden,
  // isolated out, exporter-disabled) and the pixel floor. They are
  // separate writers, so keeping them in separate arrays is what stops
  // one from undoing the other.
  const storeVisible = new Uint8Array(uids.length).fill(1);
  const sizeVisible = new Uint8Array(uids.length).fill(1);
  // Claimed by the fragment path.
  //
  // A merge holds one level of detail. When the loader decides an
  // element should be drawn FINER than that, it materialises the
  // fragment as well - and the merge went on drawing its own copy, so
  // the element existed twice, in two places, at two levels of detail,
  // and a click could only resolve one of them. Whoever draws it finer
  // wins and says so here.
  const claimed = new Uint8Array(uids.length);
  const ghost = new Uint8Array(uids.length);
  const writeVisibility = (index) => {
    const offset = index * 4 + 3;
    if (offset >= data.length) return;
    const drawn = !claimed[index] && storeVisible[index] && sizeVisible[index];
    const value = drawn ? (ghost[index] ? SLOT_GHOST_ALPHA : 1) : 0;
    if (data[offset] === value) return;
    data[offset] = value;
    texture.needsUpdate = true;
  };

  // The slot-mesh shim. setMatrixAt is how the applicator says visible or
  // hidden - it writes a snapshot matrix or a zero-scale sentinel - and
  // here that is the only part of a matrix this mesh can honour.
  mesh.isSlotMesh = true;
  mesh.batchItemCount = uids.length;
  mesh.instanceMatrix = { needsUpdate: false };
  mesh.instanceColor = { needsUpdate: false };
  mesh.setMatrixAt = (index, matrix) => {
    if (index < 0 || index >= storeVisible.length) return;
    const visible = matrix && matrix.elements ? (Math.abs(matrix.elements[0]) > 0.001
      || Math.abs(matrix.elements[1]) > 0.001 || Math.abs(matrix.elements[2]) > 0.001) : true;
    storeVisible[index] = visible ? 1 : 0;
    writeVisibility(index);
  };
  // Ghosted: drawn as the halftone while it stays visible.
  mesh.setGhostAt = (index, ghosted) => {
    if (index < 0 || index >= ghost.length) return;
    const v = ghosted ? 1 : 0;
    if (ghost[index] === v) return;
    ghost[index] = v;
    writeVisibility(index);
    markSceneChanged('merge');
  };
  // Somebody else is drawing this one.
  mesh.setItemClaimed = (index, isClaimed) => {
    if (index < 0 || index >= claimed.length) return;
    claimed[index] = isClaimed ? 1 : 0;
    writeVisibility(index);
  };
  mesh.isItemClaimed = (index) => (index >= 0 && index < claimed.length ? !!claimed[index] : false);
  // The pixel floor's half of the decision.
  mesh.setItemSizeVisible = (index, visible) => {
    if (index < 0 || index >= sizeVisible.length) return;
    sizeVisible[index] = visible ? 1 : 0;
    writeVisibility(index);
  };
  mesh.getMatrixAt = (index, matrix) => {
    if (matrix) matrix.fromArray(snapshotMatrices, index * 16);
    return matrix;
  };
  mesh.setColorAt = (index, color) => {
    const offset = index * 4;
    if (offset + 3 >= data.length || !color) return;
    if (data[offset] === color.r && data[offset + 1] === color.g && data[offset + 2] === color.b) return;
    data[offset] = color.r;
    data[offset + 1] = color.g;
    data[offset + 2] = color.b;
    texture.needsUpdate = true;
  };
  // Is this element being drawn at all? The visibility lives in the
  // texture rather than in the matrix, so a caller cannot read it off
  // getMatrixAt the way it can for a pool.
  mesh.isItemVisible = (index) => (index >= 0 && index < storeVisible.length
    ? !!(!claimed[index] && storeVisible[index] && sizeVisible[index]) : false);
  // Which element a raycast hit: every vertex of an element carries its
  // index, so any vertex of the hit face answers it.
  mesh.elementIndexAt = (vertexIndex) => {
    const attribute = geometry.getAttribute('_elementindex');
    if (!attribute || vertexIndex == null) return -1;
    return attribute.getX(vertexIndex);
  };

  if (sceneRoot) sceneRoot.add(mesh);
  // Whatever frame the file is in, this is the matrix that takes it to
  // the world: the scene root's transform composed with whatever node
  // transform the export carried. Kept current here so the first size
  // test does not read a stale one, before any frame has rendered.
  mesh.updateMatrixWorld(true);

  const bounds = itemBounds(geometry, uids.length, table);

  // What this merge costs in memory. A merged mesh is never instanced -
  // it IS the geometry - and unlike a fragment it is not in the library
  // cache, so nothing evicts it. A capture reached 1,440 MB of heap with
  // merges on and the renderer died outright on a second run, so the
  // loader needs to know what it is holding rather than find out.
  let bytes = 0;
  try {
    for (const name of Object.keys(geometry.attributes)) {
      const attr = geometry.attributes[name];
      if (attr && attr.array) bytes += attr.array.byteLength;
    }
    if (geometry.index && geometry.index.array) bytes += geometry.index.array.byteLength;
    // The visibility texture, four floats per element.
    bytes += texture.image.data.byteLength;
  } catch (e) { bytes = 0; }

  // The size prefix.
  //
  // A plain Mesh submits its whole index buffer, so hiding an element
  // through the visibility texture removes it from the picture but not
  // from the draw - measured at an aerial view of SpaceX, 12% of the
  // elements drawing and four times the triangles of the fragment path.
  // The exporter's answer is to order the elements largest first and
  // give each one the index-buffer offset just past its last triangle:
  // a size floor is then a prefix of the buffer, and one drawRange call
  // keeps everything smaller off the GPU entirely.
  const sorted = !!(table.sortedByRadius && Array.isArray(table.indexEnd)
    && table.indexEnd.length >= uids.length);
  const indexEnd = sorted ? Int32Array.from(table.indexEnd) : null;
  const totalIndices = geometry.index ? geometry.index.count : 0;
  let drawnPrefix = uids.length;

  // How many of the largest elements to draw. Everything past k never
  // reaches the GPU; what remains is still refined per element by the
  // visibility texture, which is what covers distance varying inside one
  // tile.
  // Where one element's triangles are in the index buffer.
  //
  // The same ordering that makes the size floor a prefix makes every
  // element a contiguous SLICE, which is what lets the viewer draw a
  // silhouette around one merged element: a throwaway geometry sharing
  // these buffers with its own draw range, no copy. Without it a hovered
  // element in a merged mesh gets no outline at all, and on this model
  // that is half the fragments - see buildPullOut in the viewer.
  mesh.elementRangeAt = (index) => {
    if (!indexEnd || index < 0 || index >= uids.length) return null;
    const start = index === 0 ? 0 : indexEnd[index - 1];
    const count = indexEnd[index] - start;
    return count > 0 ? { start, count } : null;
  };

  // Each element's own bounds (mesh-local centre + radius), for framing
  // one merged element rather than the whole merge (viewer ExpandMesh).
  if (bounds) { mesh.userData.itemCentres = bounds.centres; mesh.userData.itemRadii = bounds.radii; }

  const setDrawPrefix = (k) => {
    if (!indexEnd) return;
    const clamped = Math.max(0, Math.min(uids.length, k));
    if (clamped === drawnPrefix) return;
    drawnPrefix = clamped;
    geometry.setDrawRange(0, clamped === 0 ? 0 : indexEnd[clamped - 1]);
  };

  return {
    tileKey,
    lodName,
    lodIndex,
    centres: bounds ? bounds.centres : null,
    radii: bounds ? bounds.radii : null,
    bytes,
    // Ordered largest first, with a per-element index offset, so a size
    // floor can be applied as a prefix rather than as a shader discard.
    sortedByRadius: sorted,
    indexEnd,
    totalIndices,
    setDrawPrefix,
    get drawnPrefix() { return drawnPrefix; },
    setItemSizeVisible: mesh.setItemSizeVisible,
    mesh,
    meshes: [mesh],
    records,
    uids: uidSet,
    // The same uids IN ORDER, because the size prefix and the visibility
    // texture are both addressed by element index and a Set cannot
    // answer "which element is at 412".
    uidList: uids,
    indexByUid,
    // Hand the merge's copy of an element over to the fragment path.
    claim: (uid) => {
      const i = indexByUid.get(uid);
      if (i === undefined) return false;
      mesh.setItemClaimed(i, true);
      return true;
    },
    setItemClaimed: mesh.setItemClaimed,
    isItemClaimed: mesh.isItemClaimed,
    itemCount: uids.length,
    dispose() {
      if (mesh.parent) mesh.parent.remove(mesh);
      if (viewer && Array.isArray(viewer.meshes)) {
        const at = viewer.meshes.indexOf(mesh);
        if (at >= 0) viewer.meshes.splice(at, 1);
      }
      texture.dispose();
      if (mesh.material) mesh.material.dispose();
      if (mesh.geometry) mesh.geometry.dispose();
      mesh.userData = {};
      records.clear();
      uidSet.clear();
    },
  };
}

// Index by the loader's own lod numbering. Fine merges arrived later
// than the other two, so a tile whose export predates them simply has no
// entry under 'fine' and materialises its singletons as fragments.
export const MERGE_LOD_NAMES = ['fine', 'medium', 'coarse'];
